/**
 * sizes-comparison.js
 *
 * Vases range only. Two behaviours per [data-sizes-comparison] section:
 *
 * 1. Dimensions swap — while a silhouette in the Size Comparison list is
 *    hovered or focused, the dimensions line under the section heading swaps
 *    to that vase's dimensions, restoring the current product's dimensions on
 *    leave. Visual enhancement only — every list item already carries its own
 *    name and dimensions in its visually-hidden accessible name, so the swap
 *    is deliberately not announced (the subtitle is not a live region). The
 *    visually-hidden "[product] dimensions:" prefix always describes the
 *    resting state, which is what screen reader users encounter.
 *
 * 2. Mobile slider — at and below the 768px breakpoint the comparison list
 *    becomes a Swiper slider (same Swiper 11 CDN bundle as the page-builder
 *    gallery sliders), created/destroyed as the viewport crosses the
 *    breakpoint. Custom wrapperClass/slideClass point Swiper at the existing
 *    BEM markup, so the structural CSS lives in sizes-comparison.css rather
 *    than Swiper's bundle stylesheet. Slides are 64vw wide (via CSS, read by
 *    slidesPerView: 'auto') and centred, so the previous/next slides peek in
 *    at the screen edges. The slider starts on the current product's slide,
 *    and the dimensions subtitle tracks the active slide — the hover/focus
 *    swap then restores to the active slide's dimensions rather than the
 *    current product's. Off-screen slides are made inert so their product
 *    links stay out of the tab order until swiped into view.
 */

function sizesComparisonReduceMotion() {
  return (
    document.documentElement.classList.contains('reduce-motion') ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

class SizesComparison {
  /**
   * @param {HTMLElement} containerEl — the [data-sizes-comparison] element
   */
  constructor(containerEl) {
    this.containerEl = containerEl;
    this.dimensionsEl = containerEl.querySelector('[data-sizes-comparison-dimensions]');
    this.sliderEl = containerEl.querySelector('[data-sizes-comparison-slider]');
    this.swiper = null;

    if (this.dimensionsEl) {
      this.defaultText = this.dimensionsEl.textContent;
      // What _onLeave restores to: the current product's dimensions normally,
      // the active slide's dimensions while the mobile slider is live.
      this.restingText = this.defaultText;

      containerEl.addEventListener('mouseover', (e) => this._onEnter(e));
      containerEl.addEventListener('mouseout', (e) => this._onLeave(e));
      containerEl.addEventListener('focusin', (e) => this._onEnter(e));
      containerEl.addEventListener('focusout', (e) => this._onLeave(e));
    }

    this._initSliderBreakpoint();
    document.addEventListener('hattie:variant-size-change', (event) => {
      if (!this.containerEl.isConnected) return;
      if (String(event.detail.productId) !== this.containerEl.dataset.productId) return;
      this._onSizeChange(event.detail.size);
    });
  }

  _onSizeChange(size) {
    const items = Array.from(this.containerEl.querySelectorAll('[data-vase-dimensions]'));
    const currentIndex = items.findIndex((item) => item.dataset.vaseSize === size);
    if (currentIndex === -1) return;

    items.forEach((item, index) => {
      const isCurrent = index === currentIndex;
      item.classList.toggle('vases-range-comparison__item--current', isCurrent);
      const link = item.querySelector('a');
      if (!link) return;
      link.classList.toggle('vases-range-comparison__current', isCurrent);
      link.classList.toggle('vases-range-comparison__link', !isCurrent);
      if (isCurrent) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });

    this.defaultText = items[currentIndex].dataset.vaseDimensions;
    this.restingText = this.defaultText;
    if (this.dimensionsEl) this.dimensionsEl.textContent = this.defaultText;
    if (this.swiper) this.swiper.slideTo(currentIndex);
  }

  /* --- Dimensions swap ------------------------------------------------- */

  _onEnter(e) {
    if (!this.dimensionsEl) return;
    const item = e.target.closest('[data-vase-dimensions]');
    if (!item) return;
    this.dimensionsEl.textContent = item.getAttribute('data-vase-dimensions');
  }

  _onLeave(e) {
    if (!this.dimensionsEl) return;
    const item = e.target.closest('[data-vase-dimensions]');
    if (!item) return;

    // Still within the same item (moving between its children): keep the swap.
    // relatedTarget is the element being moved/focused to; null when leaving
    // the document entirely.
    if (e.relatedTarget && item.contains(e.relatedTarget)) return;

    // Entering a sibling item fires its own _onEnter immediately after this.
    this.dimensionsEl.textContent = this.restingText;
  }

  /* --- Mobile slider ---------------------------------------------------- */

  _initSliderBreakpoint() {
    // Without Swiper (CDN blocked/failed) the CSS fallback keeps the list as
    // a native horizontal scroller on mobile — nothing to do here.
    if (!this.sliderEl || typeof Swiper === 'undefined') return;

    this.mobileQuery = window.matchMedia('(max-width: 768px)');
    const sync = () => {
      if (this.mobileQuery.matches) {
        this._createSlider();
      } else {
        this._destroySlider();
      }
    };
    this.mobileQuery.addEventListener('change', sync);
    sync();

    // Keep slide speed in step with the accessibility modal's reduce-motion
    // toggle (same pattern as the page-builder gallery sliders).
    this._motionObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' && this.swiper) {
          this.swiper.params.speed = sizesComparisonReduceMotion() ? 0 : 400;
        }
      });
    });
    this._motionObserver.observe(document.documentElement, { attributes: true });
  }

  _createSlider() {
    if (this.swiper) return;

    const self = this;

    // Added before init so Swiper measures the slider layout (flex gap
    // removed, overflow hidden), not the no-JS scroll fallback.
    this.sliderEl.classList.add('is-slider');

    // Start on the current product's slide so the slider opens matching the
    // resting subtitle and the highlighted --current silhouette.
    const items = Array.from(
      this.sliderEl.querySelectorAll('.vases-range-comparison__item')
    );
    const currentIndex = items.findIndex((item) =>
      item.classList.contains('vases-range-comparison__item--current')
    );

    this.swiper = new Swiper(this.sliderEl, {
      wrapperClass: 'vases-range-comparison',
      slideClass: 'vases-range-comparison__item',
      slidesPerView: 'auto',
      // 80vw slides + centring leaves the previous and next slides peeking
      // in at both screen edges.
      centeredSlides: true,
      initialSlide: currentIndex === -1 ? 0 : currentIndex,
      // Matches the 0.625rem flex gap the list has when the slider is not
      // active — Swiper applies this as slide margins.
      spaceBetween: 10,
      watchSlidesProgress: true,
      speed: sizesComparisonReduceMotion() ? 0 : 400,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
      },
      on: {
        init() {
          self._syncSlideVisibility(this);
          self._onActiveSlideChange(this);
        },
        slideChange() {
          self._onActiveSlideChange(this);
        },
        slideChangeTransitionEnd() {
          self._syncSlideVisibility(this);
        },
      },
    });
  }

  /**
   * Point the dimensions subtitle at the active slide's vase. Like the
   * hover swap, this is a visual enhancement and deliberately unannounced —
   * each slide carries its own name and dimensions in its accessible name.
   * @param {Object} swiper — the Swiper instance
   */
  _onActiveSlideChange(swiper) {
    if (!this.dimensionsEl) return;

    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return;

    const dimensions = activeSlide.getAttribute('data-vase-dimensions');
    if (!dimensions) return;

    this.restingText = dimensions;
    this.dimensionsEl.textContent = dimensions;
  }

  _destroySlider() {
    if (!this.swiper) return;

    this.swiper.destroy(true, true);
    this.swiper = null;
    this.sliderEl.classList.remove('is-slider');

    // Back on desktop the subtitle rests on the current product's dimensions.
    if (this.dimensionsEl) {
      this.restingText = this.defaultText;
      this.dimensionsEl.textContent = this.defaultText;
    }

    // destroy() cleans inline styles and swiper classes but not the ARIA the
    // a11y module and _syncSlideVisibility added — strip it so the desktop
    // layout gets its native list semantics and tab order back.
    const list = this.sliderEl.querySelector('.vases-range-comparison');
    if (list) list.removeAttribute('aria-live');
    this.sliderEl.querySelectorAll('.vases-range-comparison__item').forEach((item) => {
      item.removeAttribute('role');
      item.removeAttribute('aria-label');
      item.removeAttribute('aria-hidden');
      item.toggleAttribute('inert', false);
    });
  }

  _syncSlideVisibility(swiper) {
    // Off-screen slides contain product links; inert keeps them out of the
    // tab order and away from screen readers until swiped into view.
    // The partially visible peeking neighbours count as visible — Swiper's
    // a11y module slides them fully into view when they receive focus.
    swiper.slides.forEach((slide) => {
      const isVisible = slide.classList.contains('swiper-slide-visible');
      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      slide.toggleAttribute('inert', !isVisible);
    });
  }
}

function initSizesComparison() {
  document.querySelectorAll('[data-sizes-comparison]').forEach((el) => {
    if (!el.dataset.sizesComparisonInitialized) {
      el.dataset.sizesComparisonInitialized = 'true';
      new SizesComparison(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizesComparison);
} else {
  initSizesComparison();
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initSizesComparison);
}
